import { Outlet, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Box, Group, Text, Button, Stack, UnstyledButton, Avatar, Menu } from '@mantine/core';
import {
  IconFiles,
  IconPlus,
  IconSettings,
  IconLogout,
  IconUser,
  IconVariable,
  IconTextSize,
  IconTemplate,
} from '@tabler/icons-react';
import { useAuth } from '@/features/auth/useAuth';

const SIDEBAR_WIDTH = 220;
const NAVY = '#415A91';
const BG = '#F2F4F7';

export type FlowFilter = 'all' | 'draft' | 'published' | 'archived' | 'trash';

const filters: { label: string; filter: FlowFilter }[] = [
  { label: 'All', filter: 'all' },
  { label: 'Draft', filter: 'draft' },
  { label: 'Published', filter: 'published' },
  { label: 'Archived', filter: 'archived' },
  { label: 'Trash', filter: 'trash' },
];

/**
 * One sidebar entry. Extracted because the four call sites previously repeated
 * the same twelve-line style object, which is what made the nav awkward to
 * extend — the shape of an item now lives in one place.
 */
function NavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      px={12}
      py={8}
      onClick={onClick}
      style={{
        borderRadius: 8,
        backgroundColor: active ? '#e8edf5' : 'transparent',
        color: active ? NAVY : '#4E5A6B',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontWeight: active ? 600 : 400,
        fontSize: 14,
        transition: 'background-color 0.15s',
      }}
    >
      {icon}
      {label}
    </UnstyledButton>
  );
}

export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();
  const activeFilter = (searchParams.get('filter') as FlowFilter) || 'all';
  // Derived from route prefixes so a new page under either section lights up
  // the right item without another boolean here. The previous
  // `isSettingsPage && !isMembersPage` pattern needed editing every time a
  // settings page was added.
  const isLibraryPage = location.pathname.startsWith('/library');
  const isSettingsPage = location.pathname.startsWith('/settings');
  const isFlowsPage = !isLibraryPage && !isSettingsPage;

  const handleFilterChange = (filter: FlowFilter) => {
    if (filter === 'all') {
      navigate('/dashboard');
    } else {
      navigate(`/dashboard?filter=${filter}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box style={{ display: 'flex', minHeight: '100vh', backgroundColor: BG }}>
      {/* Sidebar */}
      <Box
        component="nav"
        style={{
          width: SIDEBAR_WIDTH,
          minHeight: '100vh',
          backgroundColor: '#fff',
          borderRight: '1px solid #E4E7ED',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 12px',
          flexShrink: 0,
        }}
      >
        {/* Workspace name */}
        <Group gap="sm" mb="xl" px={8}>
          <Box
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              backgroundColor: NAVY,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text size="xs" fw={700} c="white">H</Text>
          </Box>
          <Box>
            <Text size="sm" fw={600} c="#2A3140" lh={1.2}>Workspace</Text>
            <Text size="xs" c="#4E5A6B" lh={1.2}>Holloway</Text>
          </Box>
        </Group>

        {/* Nav items */}
        <Stack gap={2} style={{ flex: 1 }}>
          {/* Flows nav item */}
          <NavItem
            icon={<IconFiles size={18} stroke={1.5} />}
            label="Flows"
            active={isFlowsPage}
            onClick={() => handleFilterChange('all')}
          />

          {/* Filter pills — only shown when on flows page */}
          {isFlowsPage && (
            <Stack gap={1} pl={16} mt={2} mb={4}>
              {filters.map(({ label, filter }) => (
                <UnstyledButton
                  key={filter}
                  px={10}
                  py={5}
                  onClick={() => handleFilterChange(filter)}
                  style={{
                    borderRadius: 6,
                    backgroundColor: activeFilter === filter ? '#dce4f0' : 'transparent',
                    color: activeFilter === filter ? NAVY : '#6B7685',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: activeFilter === filter ? 600 : 400,
                    fontSize: 13,
                    transition: 'background-color 0.15s',
                  }}
                >
                  {label}
                </UnstyledButton>
              ))}
            </Stack>
          )}

          {/* Divider */}
          <Box my={6} mx={12} style={{ height: 1, backgroundColor: '#E4E7ED' }} />

          {/* Library — building blocks reused across flows. Grouped under a
              heading so the next one (snippets, templates) has an obvious home
              instead of lengthening a flat list. */}
          <Text
            px={12}
            pt={4}
            pb={2}
            size="10px"
            fw={700}
            c="#8E99AB"
            tt="uppercase"
            style={{ letterSpacing: '0.08em' }}
          >
            Library
          </Text>

          <NavItem
            icon={<IconVariable size={18} stroke={1.5} />}
            label="Variables"
            active={location.pathname === '/library/variables'}
            onClick={() => navigate('/library/variables')}
          />

          <NavItem
            icon={<IconTextSize size={18} stroke={1.5} />}
            label="Rich Text Presets"
            active={location.pathname === '/library/rich-text-presets'}
            onClick={() => navigate('/library/rich-text-presets')}
          />

          <NavItem
            icon={<IconTemplate size={18} stroke={1.5} />}
            label="Templates"
            active={location.pathname === '/library/templates'}
            onClick={() => navigate('/library/templates')}
          />
        </Stack>

        {/* Bottom section — workspace configuration, reached rarely. Members
            lives inside Settings rather than beside it, so admin-only entries
            do not accumulate in the main list. */}
        <Stack gap={4} mt="auto">
          <NavItem
            icon={<IconSettings size={18} stroke={1.5} />}
            label="Settings"
            active={isSettingsPage}
            onClick={() => navigate('/settings')}
          />
        </Stack>
      </Box>

      {/* Main area */}
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top nav bar */}
        <Box
          component="header"
          style={{
            height: 56,
            backgroundColor: '#fff',
            borderBottom: '1px solid #E4E7ED',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            flexShrink: 0,
          }}
        >
          <Group gap="md">
            <Text fw={700} size="lg" c="#2A3140" style={{ letterSpacing: '-0.02em' }}>
              Holloway
            </Text>
          </Group>

          <Group gap="md">
            {/* Flows pages only. Elsewhere it would compete with the page's own
                primary action (New template, Add Preset, ...), and nothing but
                DashboardPage listens for the event, so it did nothing there. */}
            {isFlowsPage && (
              <Button
                leftSection={<IconPlus size={16} />}
                style={{ backgroundColor: NAVY }}
                radius="md"
                size="sm"
                onClick={() => {
                  // Dispatch a custom event so DashboardPage can open its modal
                  window.dispatchEvent(new CustomEvent('holloway:create-flow'));
                }}
              >
                Create New Flow
              </Button>
            )}
            <Menu position="bottom-end" shadow="md" width={180}>
              <Menu.Target>
                <Avatar
                  size={32}
                  radius="xl"
                  style={{ backgroundColor: NAVY, cursor: 'pointer' }}
                >
                  {(localStorage.getItem('user_email')?.[0] || 'U').toUpperCase()}
                </Avatar>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconUser size={14} />} onClick={() => navigate('/dashboard')}>
                  Dashboard
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item leftSection={<IconLogout size={14} />} color="red" onClick={handleLogout}>
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Box>

        {/* Page content */}
        <Box style={{ flex: 1, overflowY: 'auto' }}>
          <Outlet context={{ activeFilter }} />
        </Box>
      </Box>
    </Box>
  );
}
